import "@/App.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { NavigationProvider } from "./context/NavigationContext";
import { MillsProvider } from "./context/MillsContext";
import { ThemeProvider } from "./context/ThemeContext";
import { RfqProvider } from "./context/RfqContext";
import { CompareProvider } from "./context/CompareContext";
import { LanguageProvider, useLanguage, langFromPath, AR_PREFIX } from "./context/LanguageContext";
import RfqDrawer from "./components/RfqDrawer";
import CompareTray from "./components/CompareTray";
import SearchPalette from "./components/SearchPalette";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SalesDesk from "./components/SalesDesk";
import SeoRuntime from "./components/SeoRuntime";
import Home2030 from "./pages/Home2030";

/* Only the homepage ships in the first chunk. Every other route — and the whole admin CMS,
   which no public visitor ever needs — is split out, which is what keeps LCP and INP down. */
const HomePage = lazy(() => import("./pages/HomePage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const CustomPage = lazy(() => import("./pages/CustomPage"));
const DynamicPage = lazy(() => import("./pages/DynamicPage"));
const ProjectGalleryPage = lazy(() => import("./pages/ProjectGalleryPage"));
const TrademarkNoticePage = lazy(() => import("./pages/TrademarkNoticePage"));
const CertificationsPage = lazy(() => import("./pages/CertificationsPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const MaterialsPage = lazy(() => import("./pages/MaterialsPage"));
const MaterialCategoryPage = lazy(() => import("./pages/MaterialCategoryPage"));
const GradeDetailPage = lazy(() => import("./pages/GradeDetailPage"));
const GradeFinderPage = lazy(() => import("./pages/GradeFinderPage"));
const CataloguePage = lazy(() => import("./pages/CataloguePage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const IndustrialProductPage = lazy(() => import("./pages/IndustrialProductPage"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const IndustriesPage = lazy(() => import("./pages/IndustriesPage"));
const MarketsPage = lazy(() => import("./pages/MarketsPage"));
const MarketPage = lazy(() => import("./pages/MarketPage"));
const MarketIndustryPage = lazy(() => import("./pages/MarketIndustryPage"));
const ApplicationsPage = lazy(() => import("./pages/ApplicationsPage"));
const ApplicationPage = lazy(() => import("./pages/ApplicationPage"));
const MillsPage = lazy(() => import("./pages/MillsPage"));
const ProductFormPage = lazy(() => import("./pages/ProductFormPage"));
const InsightsPage = lazy(() => import("./pages/InsightsPage"));
const InsightDetailPage = lazy(() => import("./pages/InsightDetailPage"));
const WeightCalculatorPage = lazy(() => import("./pages/WeightCalculatorPage"));
const ComparePage = lazy(() => import("./pages/ComparePage"));
const ComparePairPage = lazy(() => import("./pages/ComparePairPage"));
const BrandComparisonPage = lazy(() => import("./pages/BrandComparisonPage"));
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const AnswersPage = lazy(() => import("./pages/AnswersPage"));
const AnswerPage = lazy(() => import("./pages/AnswerPage"));
const EditorialPolicyPage = lazy(() => import("./pages/EditorialPolicyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));

const RouteFallback = () => <div className="min-h-[60vh]" />;

function PublicLayout({ children }) {
  const location = useLocation();
  const isHeroPage = location.pathname === '/';
  return (
    <>
      <Navbar isHeroPage={isHeroPage} />
      <main className={isHeroPage ? 'min-h-screen' : 'min-h-screen pt-20'}>
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </main>
      <Footer />
      <SalesDesk />
      <RfqDrawer />
      <CompareTray />
      <SearchPalette />
    </>
  );
}

function AppRoutes() {
  const { lang } = useLanguage();
  return (
    <div key={lang}>
        <Routes>
          <Route path="/" element={<PublicLayout><Home2030 /></PublicLayout>} />
          <Route path="/classic" element={<PublicLayout><HomePage /></PublicLayout>} />
          <Route path="/about" element={<PublicLayout><DynamicPage slug="about" fallback={<AboutPage />} /></PublicLayout>} />
          <Route path="/materials" element={<PublicLayout><MaterialsPage /></PublicLayout>} />
          <Route path="/materials/:categorySlug" element={<PublicLayout><MaterialCategoryPage /></PublicLayout>} />
          <Route path="/materials/:categorySlug/:gradeSlug" element={<PublicLayout><GradeDetailPage /></PublicLayout>} />
          <Route path="/grades" element={<PublicLayout><GradeFinderPage /></PublicLayout>} />
          <Route path="/catalogue" element={<PublicLayout><CataloguePage /></PublicLayout>} />
          <Route path="/products" element={<PublicLayout><ProductsPage /></PublicLayout>} />
          <Route path="/products/:productSlug" element={<PublicLayout><IndustrialProductPage /></PublicLayout>} />
          <Route path="/industries" element={<PublicLayout><IndustriesPage /></PublicLayout>} />
          <Route path="/industries/:key" element={<PublicLayout><IndustryPage /></PublicLayout>} />
          <Route path="/mills" element={<PublicLayout><MillsPage /></PublicLayout>} />
          <Route path="/markets" element={<PublicLayout><MarketsPage /></PublicLayout>} />
          <Route path="/markets/:slug" element={<PublicLayout><MarketPage /></PublicLayout>} />
          <Route path="/markets/:slug/:industry" element={<PublicLayout><MarketIndustryPage /></PublicLayout>} />
          <Route path="/applications" element={<PublicLayout><ApplicationsPage /></PublicLayout>} />
          <Route path="/applications/:slug" element={<PublicLayout><ApplicationPage /></PublicLayout>} />
          <Route path="/compare" element={<PublicLayout><ComparePage /></PublicLayout>} />
          <Route path="/compare/:pairSlug" element={<PublicLayout><ComparePairPage /></PublicLayout>} />
          <Route path="/brands/:gradeSlug" element={<PublicLayout><BrandComparisonPage /></PublicLayout>} />
          <Route path="/forms/:formSlug" element={<PublicLayout><ProductFormPage /></PublicLayout>} />
          <Route path="/insights" element={<PublicLayout><InsightsPage /></PublicLayout>} />
          <Route path="/insights/:slug" element={<PublicLayout><InsightDetailPage /></PublicLayout>} />
          <Route path="/glossary" element={<PublicLayout><GlossaryPage /></PublicLayout>} />
          <Route path="/answers" element={<PublicLayout><AnswersPage /></PublicLayout>} />
          <Route path="/answers/:slug" element={<PublicLayout><AnswerPage /></PublicLayout>} />
          <Route path="/editorial-policy" element={<PublicLayout><EditorialPolicyPage /></PublicLayout>} />
          <Route path="/tools/steel-weight-calculator" element={<PublicLayout><WeightCalculatorPage /></PublicLayout>} />
          <Route path="/contact" element={<PublicLayout><DynamicPage slug="contact" fallback={<ContactPage />} /></PublicLayout>} />
          <Route path="/projects" element={<PublicLayout><ProjectGalleryPage /></PublicLayout>} />
          <Route path="/legal/trademarks" element={<PublicLayout><TrademarkNoticePage /></PublicLayout>} />
          <Route path="/certifications" element={<PublicLayout><CertificationsPage /></PublicLayout>} />
          <Route path="/careers" element={<PublicLayout><CareersPage /></PublicLayout>} />
          <Route path="/admin/login" element={<Suspense fallback={<RouteFallback />}><AdminLogin /></Suspense>} />
          <Route path="/admin/*" element={<Suspense fallback={<RouteFallback />}><AdminDashboard /></Suspense>} />
          <Route path="/:slug" element={<PublicLayout><CustomPage /></PublicLayout>} />
          <Route path="*" element={<PublicLayout><NotFoundPage /></PublicLayout>} />
        </Routes>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <LanguageProvider>
      <NavigationProvider>
      <MillsProvider>
      <RfqProvider>
      <CompareProvider>
      <BrowserRouter basename={langFromPath() === 'ar' ? AR_PREFIX : undefined}>
        <Toaster position="top-right" theme="light" richColors />
        <SeoRuntime />
        <AppRoutes />
      </BrowserRouter>
      </CompareProvider>
      </RfqProvider>
      </MillsProvider>
      </NavigationProvider>
      </LanguageProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
